import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid, Hidden, Box, makeStyles,
} from '@material-ui/core';
import axios, { API } from 'config/axios';
import moment from 'moment';
import useHandleResponse from 'utils/useHandleResponse';
import SearchPlace from 'views/motorcycle_rental/components/widget/SearchPlace';
import SearchPlaceModal from 'views/motorcycle_rental/components/widget/SearchPlaceModal';
import SearchTime from 'views/motorcycle_rental/components/widget/SearchTime';
import SearchTimeModal from 'views/motorcycle_rental/components/widget/SearchTimeModal';
import GeoWidgetAndFiltersDivider from 'views/motorcycle_rental/components/GeoWidgetAndFiltersDivider';
import { withTranslation } from 'config/i18n';

const useStyles = makeStyles((theme) => ({
  WidgetRoot: {
    height: '100%',
    flexWrap: 'nowrap',
  },
  searchPickupGridWidget: {
    transition: 'all .2s',
    flex: '1 1 59%',
    '& .MuiGrid-root': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiAutocomplete-root': {
      '& .MuiFormControl-root': {
        '& .MuiInputBase-root': {
          paddingLeft: theme.spacing(2),
          [theme.breakpoints.up('lg')]: {
            paddingLeft: 0,
          },
          '& .MuiInputBase-input': {
            fontSize: theme.typography.pxToRem(14),
            paddingTop: `${theme.spacing(0.4)} !important`,
          },
          '& .arrowIconOverride': {
            width: theme.spacing(2.5),
            height: theme.spacing(2.5),
          },
          '& .MuiInputAdornment-root': {
            alignItems: 'center',
            marginRight: 0,
            width: theme.spacing(2.5),
            height: theme.spacing(2.5),
            '& .locationIconOverride': {
              fontSize: theme.typography.pxToRem(14),
              fill: theme.palette.primary.main,
              position: 'relative',
              bottom: theme.spacing(0.2),
              width: theme.spacing(2),
              height: theme.spacing(2),
            },
          },
        },
        '& .MuiFormLabel-root': {
          color: theme.palette.grey[300],
          fontSize: theme.typography.pxToRem(14),
          left: theme.spacing(3),
          [theme.breakpoints.up('lg')]: {
            '&.MuiInputLabel-shrink': {
              transform: 'translate(0, 1.5px) scale(0.85) ',
            },
          },
          [theme.breakpoints.down('md')]: {
            left: `${theme.spacing(5)} !important`,
            top: `${theme.spacing(1.5)} !important`,
          },
          '.Mui-focused &': {
            color: theme.palette.primary.main,
          },
        },
      },
    },
  },
  searchTimeContainer: {
    flex: '1 1 40%',
  },
}));

let controller;
let timeoutId;
const Widget = (props) => {
  const { rentalData, handleFieldChange, t } = props;
  const classes = useStyles();
  const { handleError } = useHandleResponse();

  // states
  const [locations, setLocations] = useState({
    pickup: [],
    dropoff: [],
  });
  const [sameDropoff, setSameDropoff] = useState(true);
  const [dates, setDates] = useState({
    start: rentalData?.selected_filters?.pickup_date
      ? moment(rentalData?.selected_filters?.pickup_date)
      : null,
    end: rentalData?.selected_filters?.dropoff_date
      ? moment(rentalData?.selected_filters?.dropoff_date)
      : null,
  });
  // handlers

  const getDropoffLocations = (params) => axios
    .get(API.rentals.nearbyDropoffLocations, {
      params,
    })
    .then((res) => {
      setLocations((prevData) => ({
        ...prevData,
        dropoff: res.data.nearby_dropoff_locations,
      }));
    })
    .catch((err) => {
      if (err.message !== 'canceled') {
        handleError(err);
      }
    });
    const getPickupLocations = (query) => {
      axios
        .get(API.rentals.pickupPlaces, {
          signal: controller.signal,
          params: {
            q: query,
          },
        })
        .then((res) => {
          // Add selected place to the pickup places if it's not already there
          const selectedPlace = rentalData?.selected_filters?.pickup_place;
          if (selectedPlace && !res.data.pickup_places.some(place => place.id === selectedPlace.id)) {
            res.data.pickup_places.unshift({
              id: selectedPlace.id,
              name: `${selectedPlace.name}, ${selectedPlace.country?.name || ''}`.trim(),
              slug: selectedPlace.slug,
              display_name: selectedPlace.name,
              short_name: selectedPlace.short_name
            });
          }

          setLocations((prevData) => ({
            ...prevData,
            pickup: res.data.pickup_places,
          }));
        })
        .catch((err) => {
          if (err.message !== 'canceled') {
            handleError(err);
          }
        });
    };
  const pickupLocation = useMemo(() => {
    const pickupLocations = [
      { id: null, name: t('fe_es_geo_widget:place.current_location') },
      ...(locations.pickup || []),
    ];

    const pickupLoc = pickupLocations.find(
      (location) => location.id === rentalData?.selected_filters.pickup_place?.id,
    );

    if (pickupLoc) {
      pickupLoc.display_name = rentalData?.selected_filters.pickup_place.name;
      return pickupLoc;
    }
    return null;
  }, [locations, rentalData, rentalData?.selected_filters.pickup_place]);

  const handlePickupInputChange = (newValue, reason) => {
    if (reason === 'reset') return;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (newValue?.length > 2) {
      if (controller) {
        controller.abort();
      }
      controller = new AbortController();
      timeoutId = setTimeout(() => {
        getPickupLocations(newValue);
      }, 100);
    }

    if (newValue?.length === 0 && pickupLocation) {
      setSameDropoff(true);
      setDates({ start: null, end: null });
      handleFieldChange({
        dropoff_location_id: null,
        pickup_place_slug: null,
        pickup_date: null,
        dropoff_date: null,
        page: 1,
        lat: null,
        lng: null,
      });
    }
  };
  const handlePickupAutoCompleteChange = (newValue, currentLocation) => {
    if (currentLocation) {
      const { lat, lng } = newValue;
      handleFieldChange({
        lat,
        lng,
        pickup_place_slug: null,
        page: 1,
      });
      getDropoffLocations({ lat, lng });
      return null;
    }
    handleFieldChange({
      pickup_place_slug: newValue?.slug,
      page: 1,
      lat: null,
      lng: null,
    });
    getDropoffLocations({ place_id: newValue?.id });
    return null;
  };
  const handleReturnToDifferentLocationClick = (checked) => {
    setSameDropoff(checked);
  };
  const handleDropoffAutoCompleteChange = (newValue) => {
    handleFieldChange({
      dropoff_location_id: newValue?.id,
      page: 1,
    });
  };
  const handleDropoffFieldClear = () => {
    setSameDropoff(true);
    handleFieldChange({
      dropoff_location_id: null,
    });
  };

  const dropoffLocation = useMemo(() => {
    const dropoffLoc = locations.dropoff.find(
      (location) => location.id === rentalData?.selected_filters.dropoff_location?.id,
    );
    if (dropoffLoc) {
      dropoffLoc.display_name = rentalData?.selected_filters.dropoff_location?.name;
      return dropoffLoc;
    }
    return null;
  }, [locations, rentalData]);
  const placeWidgetProps = useMemo(
    () => ({
      pickupLocations: locations.pickup,
      selectedPickupPlace: pickupLocation,
      dropoffGeos: locations.dropoff,
      selectedDropoffGeo: dropoffLocation,
      onChange: (newValue) => handlePickupAutoCompleteChange(newValue, false),
      getCurrentLocation: (currentLocation) => handlePickupAutoCompleteChange(currentLocation, true),
      onInputChange: handlePickupInputChange,
      onDropoffChange: handleDropoffAutoCompleteChange,
      onClearDropoff: handleDropoffFieldClear,
      ShowCurrentLocationButton: true,
      sameLocation: sameDropoff,
      handleSameLocationClick: handleReturnToDifferentLocationClick,
    }),
    [locations, sameDropoff, pickupLocation, dropoffLocation],
  );

  const timeWidgetProps = useMemo(
    () => ({
      startDate: dates.start,
      endDate: dates.end,
      disabled: !pickupLocation,
      minimumDate: rentalData?.filters?.min_pickup_date,
      showCalendar: pickupLocation,
      onChange: (newDates) => {
        setDates((prevState) => ({
          ...prevState,
          start: newDates.startDate,
          end: newDates.endDate,
        }));
        handleFieldChange({
          pickup_date: newDates.startDate?.format('YYYY-MM-DD'),
          dropoff_date: newDates.endDate?.format('YYYY-MM-DD'),
        });
      },
      handleClearRangePicker: () => {
        setDates({ start: null, end: null });
        handleFieldChange({
          pickup_date: null,
          dropoff_date: null,
        });
      },
    }),
    [rentalData.minimumDate, pickupLocation, dates],
  );

  useEffect(() => {
    if (rentalData?.selected_filters?.pickup_place) {
      if (!pickupLocation?.id) {
        if (controller) {
          controller.abort();
        }
        controller = new AbortController();
        getPickupLocations(rentalData?.selected_filters?.pickup_place?.name);
        const params = {};
        if (rentalData?.selected_filters.pickup_place?.id === null) {
          params.lat = rentalData?.selected_filters.pickup_place?.location?.lat;
          params.lng = rentalData?.selected_filters.pickup_place?.location?.lng;
        } else {
          params.place_id = rentalData?.selected_filters.pickup_place?.id;
        }
        getDropoffLocations(params);
      }
    }
    if (typeof rentalData?.selected_filters?.dropoff_location?.id === 'number') {
      if (!dropoffLocation) {
        // if no id this means it entered the first check and this one is redundent
        if (rentalData?.selected_filters?.pickup_place?.id) {
          getDropoffLocations({
            place_id: rentalData?.selected_filters?.pickup_place?.id,
          });
        }
      }
      if (sameDropoff) {
        handleReturnToDifferentLocationClick(false);
      }
    }
  }, [rentalData?.selected_filters?.pickup_place, rentalData?.selected_filters?.dropoff_location]);
  return (
    <Grid container className={classes.WidgetRoot}>
      <Hidden mdDown>
        <Grid item className={classes.searchPickupGridWidget}>
          <SearchPlace data={placeWidgetProps} hideDropoff={false} />
        </Grid>
        <GeoWidgetAndFiltersDivider />
        <Grid item className={classes.searchTimeContainer}>
          <SearchTime data={timeWidgetProps} />
        </Grid>
        <GeoWidgetAndFiltersDivider />
      </Hidden>
      <Hidden lgUp>
        {/* maxWidth="50%" */}
        <Box display="flex" height="100%" flex={1} className={classes.searchPickupGridWidget}>
          <SearchPlaceModal
            timeWidgetProps={timeWidgetProps}
            placeWidgetProps={placeWidgetProps}
            hideDropoff
          />
        </Box>
        <Hidden smDown>
          <Box display="flex" height="100%">
            <GeoWidgetAndFiltersDivider />
          </Box>
          <Box width="50%">
            <SearchTimeModal data={timeWidgetProps} disabled={timeWidgetProps.disabled} />
          </Box>
        </Hidden>
      </Hidden>
    </Grid>
  );
};
export default withTranslation(['fe_es_geo_widget'])(Widget);
